import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { startAuthentication } from '@simplewebauthn/browser';
import { Fingerprint, Mail, ShieldAlert } from 'lucide-react';
import { Dialog } from '../ui';
import { formatOtpCodeInput } from './otpCode';
import { useRetryWait } from '../ui/useRetryWait';
import {
  requestStepUpCode,
  stepUpAssertionOptions,
  stepUpDoors,
  verifyStepUpCode,
  verifyStepUpPasskey,
} from '../../lib/api/stepUp';
import {
  reportReauthenticated,
  setReauthenticationRequiredHandler,
  type StaleAuthentication,
} from '../../lib/api/reauthenticationSignal';
import { passkeysSupported } from '../../lib/api/passkeys';
import { useLocale, type MessageKey } from '../../i18n';
import type { LocaleContextValue } from '../../i18n';

// ═══════════════════════════════════════════════════════════════════
// «PROVE YOU ARE STILL THERE» — THE PROMPT (#750 M2b-3).
//
// Everything else in this chain has existed for a while and done nothing,
// because the last link was missing:
//
//   server      403 `reauthentication_required` + `windowSeconds` from
//               `requireCapability` and `requireFreshness`
//   transport   `http.ts` reports it to `reauthenticationSignal`
//   ceremonies  `stepUp.ts` — e-mail code and passkey, bearer swap included
//   ⟶ PROMPT    nothing registered a handler, and no component existed
//
// `stepUp.ts` says so itself: «WHAT THIS MODULE DOES NOT DECIDE: WHEN to
// prompt. That is the prompt's business, driven by `reauthenticationSignal`.»
// This is that business, and nothing more — it decides when to ask and what to
// say, and the ceremonies stay where they are.
//
// ── WHY IT MATTERS NOW RATHER THAN WHENEVER ────────────────────────
//
// A club session is twelve hours and the freshness window is twelve hours, so
// a session old enough to be stale has already expired and freshness cannot
// bite. A REMEMBERED personal session is thirty days (#1091: the native app,
// or «Husk meg på denne enheten»), and for somebody who also administers a
// club this is what opens once their sign-in is more than twelve hours old —
// instead of a 403 with no way back but signing out. `reauthenticationSignal`'s
// own comment rules that out: «they would have to do more, not less, to get
// back to where they were».
//
// It landed FIRST, deliberately, before any door issued the long session.
// Its tests drive the signal directly for that reason.
//
// ── WHAT IT DOES NOT DO ────────────────────────────────────────────
//
// It does not retry the request that was refused. The person asked for
// something, was asked to prove themselves, and is returned to the screen they
// were on to ask again — which is honest about what happened and needs no
// replay buffer holding a request whose body may no longer be what they want.
// Automatic retry is a product decision and a bigger change.
//
// It does not sign anybody out, ever. A stale session is live and belongs to
// the right person; `onUnauthorized` owns the case where identity is gone.
// ═══════════════════════════════════════════════════════════════════

/**
 * The default `Dialog` backdrop, lifted above the highest overlay in the app
 * (the booking modals' confirmations, z-index 10002). Exported for the test.
 */
export const STEP_UP_BACKDROP =
  'fixed inset-0 z-[10100] flex items-center justify-center p-4 bg-ink/40 backdrop-blur-sm';

type Step =
  /** Closed. The only state the component has when nothing is stale. */
  | { name: 'idle' }
  /** Open, offering the doors this account can actually use. */
  | { name: 'choose'; windowSeconds: number | null; selfService: boolean; passkey: boolean }
  /** A code was asked for and is being typed. */
  | { name: 'code'; windowSeconds: number | null; selfService: boolean; resent: boolean }
  /** Proven. Shown briefly so the dismissal is a result and not a disappearance. */
  | { name: 'done' };

/**
 * Server CODES → a sentence, in this browser's language.
 *
 * The codes are the server's and are never translated (CLAUDE.md); the words
 * are chosen here. `session_changed` is `stepUp.ts`'s own — the ceremony
 * completed under a session that is now gone — and it is the one refusal that
 * is not the person's fault and not retryable.
 */
function refusalText(
  t: LocaleContextValue['t'],
  // `ok` IS IN THE TYPE for a reason beyond documentation: a parameter whose
  // every property is optional is a «weak type», and TypeScript refuses to
  // accept `{ok: true}` against it because the two share no property at all.
  refused: { ok: boolean; code?: string },
): string {
  // ── WHY THE OUTCOME IS PASSED AND NOT `outcome.code` ───────────────
  //
  // `stepUp.ts`'s outcomes discriminate on a BOOLEAN — `{ok: true} | {ok:
  // false; code: string}` — and this repo compiles with `strict: false`
  // (`tsconfig.base.json`), where TypeScript does NOT narrow that union on
  // `!outcome.ok`: literal types widen, so `.code` is an error on the
  // un-narrowed union. Verified rather than guessed; the same code compiles
  // under `--strict` in isolation.
  //
  // Reading the optional field here is the narrowing that works in both
  // modes, and it fails safe: an outcome with no code reads as generic rather
  // than as a successful one.
  const code = refused.code ?? '';
  const mapped: Record<string, MessageKey> = {
    invalid_code: 'stepUp.error.invalid_code',
    expired_code: 'stepUp.error.invalid_code',
    no_passkey_registered: 'stepUp.error.no_passkey',
    no_local_credential: 'stepUp.error.no_local_credential',
    session_changed: 'stepUp.error.session_changed',
    unauthorized: 'stepUp.error.session_changed',
  };
  const key = mapped[code];
  return key ? t(key) : t('stepUp.error.generic');
}

/** «innen 12 timer» — the server's own window, not a number copied here. */
function windowText(t: LocaleContextValue['t'], seconds: number | null): string {
  if (seconds == null) return t('stepUp.lead.noWindow');
  const hours = Math.round(seconds / 3600);
  return hours >= 1
    ? t('stepUp.lead.hours', { hours })
    : t('stepUp.lead.minutes', { minutes: Math.max(1, Math.round(seconds / 60)) });
}

export interface StepUpPromptProps {
  /**
   * Injectable for tests, and only for tests. Defaults to the real module —
   * the component is mounted once by the app and never configured.
   */
  deps?: Partial<{
    doors: typeof stepUpDoors;
    requestCode: typeof requestStepUpCode;
    verifyCode: typeof verifyStepUpCode;
    assertionOptions: typeof stepUpAssertionOptions;
    verifyPasskey: typeof verifyStepUpPasskey;
    authenticate: typeof startAuthentication;
    supported: typeof passkeysSupported;
  }>;
}

export function StepUpPrompt({ deps = {} }: StepUpPromptProps) {
  const doors = deps.doors ?? stepUpDoors;
  const requestCode = deps.requestCode ?? requestStepUpCode;
  const verifyCode = deps.verifyCode ?? verifyStepUpCode;
  const assertionOptions = deps.assertionOptions ?? stepUpAssertionOptions;
  const verifyPasskey = deps.verifyPasskey ?? verifyStepUpPasskey;
  const authenticate = deps.authenticate ?? startAuthentication;
  const supported = deps.supported ?? passkeysSupported;

  const { t } = useLocale();
  const [step, setStep] = useState<Step>({ name: 'idle' });
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // ── A SPENT BUDGET SAYS HOW LONG (#778 Q7) ─────────────────────────
  //
  // The server answers `too_many_attempts` — it never sent the `throttled`
  // this used to map, so a throttled step-up read «Det gikk ikke å bekrefte»,
  // the generic sentence, with no wait at all. The mail budget (send a code)
  // and the mint budget (passkey) are separate, so each holds only its own
  // button.
  const wait = useRetryWait<'send' | 'passkey'>();
  const shownError = error ?? wait.notice;
  const errorId = useId();
  const leadId = useId();

  // THE LIVE STEP, FOR THE HANDLER. The handler is registered once and must
  // not be re-registered on every state change — a re-register between a 403
  // and its delivery would drop the notice — so it reads the step through a ref
  // rather than closing over it.
  const current = useRef<Step>(step);
  current.current = step;

  // ── THE RESERVATION IS SYNCHRONOUS, AND THAT IS THE POINT (#827 review) ──
  //
  // The first version checked `current.current.name !== 'idle'` and then
  // AWAITED the doors lookup before setting any state. Two stale answers
  // arriving in that window — which is the ordinary case, because a page loads
  // several protected resources at once and every one of them can be refused —
  // both saw `idle`, both asked `/me/passkeys`, and the slower one's
  // `setStep({name:'choose'})` threw the person back to the first screen,
  // discarding a code they were halfway through typing.
  //
  // A ref, not state: it has to be true BEFORE the first await, and a state
  // update is not visible to a second handler call in the same tick. Held
  // until the dialog closes, which is also what makes the «already asking»
  // rule hold for the whole ceremony rather than only for the lookup.
  const reserved = useRef(false);
  // AND NOTHING IS SET AFTER UNMOUNT.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // ── DISMISSING INVALIDATES WHAT IS IN FLIGHT (#827 re-review) ──────
  //
  // `alive` covers unmount, and dismissing does NOT unmount this component —
  // which left a hole the review found and which my first fix should have
  // closed, because «ignore late completions after dismissal or a newer
  // ceremony» was in the original required list and I implemented only the
  // unmount half.
  //
  // The sequence: a 403 opens the dialog, somebody clicks «send me a code»,
  // the request is still in flight, they press Escape — the #813 `Dialog`
  // allows that even while busy, deliberately — and then the request resolves.
  // `askForCode` went on to `setStep({name: 'code'})` from the old render's
  // state, REOPENING a dialog nobody asked to see, with no new stale notice
  // behind it. A late `verifyCode` or passkey verification did the same with
  // `done`. And a stale `setBusy(false)` or `setError` could land on a NEWER
  // ceremony, enabling a button mid-request or showing somebody an error that
  // belongs to a dialog they already closed.
  //
  // A MONOTONIC GENERATION, bumped by both opening and closing. Every ceremony
  // captures it once and writes no state after it has moved. This is a CLIENT
  // UI guard and nothing else: a step-up that completed on the server stays
  // completed — the bearer was already rotated inside `stepUp.ts`, which
  // guards the session incarnation itself — so what is discarded here is a
  // screen update, never a proof.
  const ceremony = useRef(0);

  /**
   * A gate for one run of one ceremony: false once the dialog has moved on.
   *
   * Captured at entry, consulted after every await and before every state
   * write — including the `busy` clear, because clearing it for a dead
   * generation is exactly how a stale completion reaches a live dialog.
   */
  const gate = () => {
    const generation = ceremony.current;
    return () => alive.current && generation === ceremony.current;
  };

  const open = useCallback(
    async (info: StaleAuthentication) => {
      if (reserved.current || current.current.name !== 'idle') return;
      reserved.current = true;
      ceremony.current += 1;
      const live = gate();
      setError(null);
      setCode('');
      // ASKED, NOT ASSUMED (`stepUpDoors`): offering a passkey button to an
      // account with no passkey produces a 409 and a dead end. `supported()`
      // is the browser half of the same question.
      //
      // AND A FAILED LOOKUP MUST NOT SWALLOW THE WHOLE PROMPT (#827 review).
      // `apiFetch` RETHROWS a network error — `http.ts` catches it only to
      // report that the server is silent, then `throw err` — and
      // `stepUpDoors` does not convert that into an outcome. An unhandled
      // rejection here meant no dialog appeared at all, so a stale
      // administrator on a flaky connection had no path to an e-mail code.
      // Which matters more here than almost anywhere: the measured dev
      // outages (#802) are blackholes, not error pages.
      //
      // So the lookup is best-effort and its failure degrades to the door
      // every account with a local credential has.
      let available = { passkey: false };
      try {
        if (supported()) available = await doors();
      } catch {
        available = { passkey: false };
      }
      if (!live()) return;
      setStep({
        name: 'choose',
        windowSeconds: info.windowSeconds,
        selfService: info.selfService === true,
        passkey: available.passkey,
      });
    },
    [doors, supported],
  );

  useEffect(() => {
    setReauthenticationRequiredHandler((info) => void open(info));
    return () => setReauthenticationRequiredHandler(null);
  }, [open]);

  const close = () => {
    // INVALIDATE FIRST. Everything below is undone by a late completion unless
    // the generation has already moved, and `busy` is cleared here so a
    // ceremony abandoned mid-request cannot leave the NEXT dialog inert —
    // its own `busy` clear will be refused for being a generation behind.
    ceremony.current += 1;
    setBusy(false);
    setStep({ name: 'idle' });
    setCode('');
    setError(null);
    wait.clear();
    // RELEASED HERE AND ONLY HERE. While the dialog is open the reservation is
    // what refuses a second notice; dismissing it is what makes the next stale
    // answer able to ask again.
    reserved.current = false;
  };

  const askForCode = async () => {
    if (busy || step.name === 'idle' || step.name === 'done' || wait.waiting('send')) return;
    const live = gate();
    setBusy(true);
    setError(null);
    wait.release('send');
    // ── ONE GATED TAIL, NOT A `finally` (#827 re-review) ──────────────
    //
    // A rejection used to leave this busy for ever — `setBusy(false)` after
    // the await never ran when `apiFetch` threw, and every control here is
    // `disabled={busy}`. A `finally` fixed that and introduced the next
    // problem: it cleared `busy` whatever had happened to the dialog
    // meanwhile, including for a ceremony that had been dismissed or
    // superseded. So the catch produces an outcome, and ONE guarded tail does
    // every state write — `close()` is what clears `busy` for an abandoned
    // ceremony.
    let outcome: { ok: boolean; code?: string; retryAfterSeconds?: number };
    try {
      outcome = await requestCode();
    } catch {
      outcome = { ok: false, code: '' };
    }
    if (!live()) return;
    setBusy(false);
    if (!outcome.ok && outcome.code === 'too_many_attempts') {
      wait.start('send', outcome.retryAfterSeconds ?? 0);
      return;
    }
    if (!outcome.ok) {
      setError(refusalText(t, outcome));
      return;
    }
    setStep({
      name: 'code',
      windowSeconds: step.windowSeconds,
      selfService: step.selfService,
      resent: step.name === 'code',
    });
  };

  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || code.length === 0) return;
    const live = gate();
    setBusy(true);
    setError(null);
    let outcome: { ok: boolean; code?: string };
    try {
      outcome = await verifyCode(code);
    } catch {
      // A DROPPED REQUEST IS NOT A WRONG CODE. The code may well be right and
      // still unspent, so the message is the generic retryable one and the
      // field keeps what was typed.
      outcome = { ok: false, code: '' };
    }
    // A LATE SUCCESS IS DISCARDED ON SCREEN AND KEPT ON THE SERVER. If this
    // verification completed after the person dismissed the dialog, the
    // step-up really did happen — `stepUp.ts` rotated the bearer — and all
    // that is dropped is a `done` panel nobody is looking at.
    if (!live()) return;
    setBusy(false);
    if (!outcome.ok) {
      setError(refusalText(t, outcome));
      return;
    }
    setStep({ name: 'done' });
    reportReauthenticated();
  };

  const proveWithPasskey = async () => {
    if (busy || wait.waiting('passkey')) return;
    const live = gate();
    setBusy(true);
    setError(null);
    wait.release('passkey');
    // ── THREE AWAITS, ONE GATED TAIL (#827 and its re-review) ─────────
    //
    // Each of the three can throw — `assertionOptions` and `verifyPasskey`
    // through `apiFetch`, `authenticate` through the platform — and each is a
    // point at which the dialog may already have been dismissed. So the body
    // decides WHAT to say and writes nothing; the tail checks the generation
    // once and does every write.
    //
    // `proved` and `failure` rather than early `setError`/`setStep` calls,
    // because an early write is exactly what the guard has to come before.
    let failure: string | null = null;
    let proved = false;
    let throttledFor: number | null = null;
    try {
      const options = await assertionOptions();
      if (!live()) return;
      // Read through the optional fields — `strict: false` does not narrow a
      // boolean discriminant (see `refusalText`).
      const refused = options as { ok: boolean; code?: string; retryAfterSeconds?: number };
      if (!refused.ok && refused.code === 'too_many_attempts') {
        throttledFor = refused.retryAfterSeconds ?? 0;
      } else if (!options.ok) {
        failure = refusalText(t, options);
      } else {
        let assertion: unknown;
        let cancelled = false;
        try {
          assertion = await authenticate({ optionsJSON: options.options as never });
        } catch (err) {
          cancelled = true;
          const name = (err as { name?: string } | null)?.name;
          // CANCELLING IS NOT FAILING. The person dismissed the platform
          // prompt; telling them something went wrong would be a lie, and the
          // dialog stays open so they can choose the other door.
          if (name !== 'NotAllowedError' && name !== 'AbortError') {
            failure = t('stepUp.error.generic');
          }
        }
        if (!live()) return;
        if (!cancelled) {
          const outcome = await verifyPasskey(assertion);
          if (!live()) return;
          if (outcome.ok) proved = true;
          else failure = refusalText(t, outcome);
        }
      }
    } catch {
      // A thrown request, not a refusal: retryable, and both doors stay open.
      failure = t('stepUp.error.generic');
    }
    if (!live()) return;
    setBusy(false);
    if (throttledFor !== null) {
      wait.start('passkey', throttledFor);
      return;
    }
    if (failure) {
      setError(failure);
      return;
    }
    if (proved) {
      setStep({ name: 'done' });
      reportReauthenticated();
    }
  };

  if (step.name === 'idle') return null;

  // ABOVE EVERY OTHER OVERLAY (#284). The prompt answers a refusal of whatever
  // the person just pressed, and that is often inside a modal: the calendar's
  // booking modal and its confirmations sit at z-index 9999–10002, so at the
  // default `z-50` this opened BEHIND them — present, focused, and invisible,
  // which is the «the button does nothing» this prompt exists to end. Found in
  // the browser on «Avbestill og refunder».
  return (
    <Dialog
      heading={t('stepUp.title')}
      onClose={close}
      describedBy={leadId}
      backdropClassName={STEP_UP_BACKDROP}
    >
      <div data-testid="step-up-prompt" className="flex flex-col gap-4">
        <p id={leadId} className="text-sm text-ink-2 inline-flex items-start gap-2">
          <ShieldAlert size={16} className="mt-0.5 flex-shrink-0 text-ink-3" aria-hidden="true" />
          <span>
            {step.name === 'done'
              ? t('stepUp.lead.done')
              : `${t(step.selfService ? 'stepUp.lead.selfService' : 'stepUp.lead')} ${windowText(t, step.windowSeconds)}`}
          </span>
        </p>

        {step.name === 'choose' && (
          <div className="flex flex-col gap-2">
            {step.passkey && (
              <button
                type="button"
                className="btn-lime w-full justify-center gap-2 py-2.5"
                disabled={busy || wait.waiting('passkey')}
                onClick={() => void proveWithPasskey()}
                data-testid="step-up-passkey"
              >
                <Fingerprint size={18} aria-hidden="true" />
                {busy ? t('stepUp.working') : t('stepUp.usePasskey')}
              </button>
            )}
            <button
              type="button"
              className={`${step.passkey ? 'btn-outline' : 'btn-lime'} w-full justify-center gap-2 py-2.5`}
              disabled={busy || wait.waiting('send')}
              onClick={() => void askForCode()}
              data-testid="step-up-send-code"
            >
              <Mail size={16} aria-hidden="true" />
              {busy ? t('stepUp.working') : t('stepUp.sendCode')}
            </button>
          </div>
        )}

        {step.name === 'code' && (
          <form onSubmit={submitCode} className="flex flex-col gap-3">
            <label className="block">
              <span className="block text-xs font-mono uppercase tracking-wider text-ink-3 mb-1.5">
                {t('stepUp.codeLabel')}
              </span>
              <input
                type="text"
                value={code}
                // NORMALISED, NOT TRUNCATED — `otpCode.ts`, and no `maxLength`,
                // because the attribute cut a pasted «123 456» to six
                // characters before any handler ran (#814).
                onChange={(e) => setCode(formatOtpCodeInput(e.target.value))}
                autoComplete="one-time-code"
                inputMode="numeric"
                aria-invalid={error ? true : undefined}
                aria-describedby={shownError ? errorId : undefined}
                className="input w-full text-center tracking-[0.3em] text-lg"
                disabled={busy}
                data-testid="step-up-code"
              />
            </label>
            <button
              type="submit"
              className="btn-lime w-full justify-center py-2.5"
              disabled={busy || code.length === 0}
              data-testid="step-up-verify"
            >
              {busy ? t('stepUp.working') : t('stepUp.verify')}
            </button>
            {step.resent && !shownError && (
              <p className="text-xs text-ink-3">{t('stepUp.resent')}</p>
            )}
            <button
              type="button"
              className="w-full text-center text-[12px] text-ink-3 hover:text-ink py-1"
              disabled={busy || wait.waiting('send')}
              onClick={() => void askForCode()}
            >
              {t('stepUp.resend')}
            </button>
          </form>
        )}

        {step.name === 'done' && (
          <button
            type="button"
            className="btn-lime w-full justify-center py-2.5"
            onClick={close}
            data-testid="step-up-close"
          >
            {t('stepUp.continue')}
          </button>
        )}

        {shownError && (
          <p id={errorId} role="alert" className="text-xs text-rose" data-testid="step-up-error">
            {shownError}
          </p>
        )}
      </div>
    </Dialog>
  );
}
